import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, BadgeCheck, Wallet, Plane, ShieldCheck } from "lucide-react";
import { VerifiedBadge, Rating } from "../components/common";
import { Input } from "../components/ui/input";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";

export default function Profile() {
  const { user, logout, updateUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user.name, bio: user.bio || "", city: user.city || "", phone: user.phone || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch("/auth/profile", form); updateUser(data); toast.success("Profil disimpan"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const isBawainer = user.role === "bawainer";
  const links = [
    isBawainer && { to: "/wallet", label: t("nav_wallet"), icon: Wallet },
    isBawainer && { to: "/my-trips", label: t("nav_trips"), icon: Plane },
    { to: "/verification", label: "Verification", icon: ShieldCheck },
    { to: "/safety", label: "Safety Center", icon: BadgeCheck },
  ].filter(Boolean);

  return (
    <div className="max-w-2xl space-y-5">
      <h1 className="font-heading font-extrabold text-2xl text-navy">{t("nav_profile")}</h1>

      <div className="bg-white rounded-3xl p-6 shadow-soft border border-black/5">
        <div className="flex items-center gap-4">
          <img src={user.avatar} alt={user.name} className="w-16 h-16 rounded-2xl object-cover" />
          <div>
            <div className="flex items-center gap-2"><span className="font-heading font-bold text-lg text-navy">{user.name}</span><VerifiedBadge status={user.verified_status} showLabel={false} /></div>
            <div className="text-sm text-muted-foreground capitalize">{user.role}</div>
            {isBawainer && <Rating value={user.rating} count={user.reviews_count} className="mt-1" />}
          </div>
        </div>
        <span className="inline-block mt-3 text-xs bg-cloud rounded-full px-3 py-1 text-navy font-semibold uppercase">{user.membership_tier} member</span>
      </div>

      <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5 space-y-3">
        <div><label className="text-sm font-semibold text-ink">Nama</label><Input data-testid="profile-name" value={form.name} onChange={set("name")} className="rounded-xl mt-1" /></div>
        <div><label className="text-sm font-semibold text-ink">Bio</label><Input data-testid="profile-bio" value={form.bio} onChange={set("bio")} className="rounded-xl mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm font-semibold text-ink">Kota</label><Input data-testid="profile-city" value={form.city} onChange={set("city")} className="rounded-xl mt-1" /></div>
          <div><label className="text-sm font-semibold text-ink">HP</label><Input data-testid="profile-phone" value={form.phone} onChange={set("phone")} className="rounded-xl mt-1" /></div>
        </div>
        <button onClick={save} disabled={busy} data-testid="save-profile" className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 transition-colors">{t("save")}</button>
      </div>

      <div className="bg-white rounded-3xl shadow-soft border border-black/5 divide-y divide-black/5">
        {links.map((l) => { const Icon = l.icon; return (
          <button key={l.to} onClick={() => navigate(l.to)} data-testid={`profile-link-${l.to.slice(1)}`}
            className="w-full flex items-center gap-3 p-4 text-left hover:bg-cloud transition-colors">
            <Icon size={18} className="text-navy" /> <span className="font-medium text-ink">{l.label}</span>
          </button>
        ); })}
        <button onClick={() => { logout(); navigate("/"); }} data-testid="profile-logout"
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-cloud transition-colors text-red-600">
          <LogOut size={18} /> <span className="font-medium">{t("logout")}</span>
        </button>
      </div>
    </div>
  );
}
